import Apple from "@/assets/svg/apple.svg";
import Google from "@/assets/svg/google.svg";
import { useAppForm, withForm } from "@/hooks/form";
import { useMininumDeviceWidthBreakpoint } from "@/hooks/media";
import { BBIsomorphicAuthClient } from "@/lib/backend/auth";
import { m } from "@/paraglide/messages";
import { Button } from "@bidbattles/bloks/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@bidbattles/bloks/components/ui/dialog";
import { Divider } from "@bidbattles/bloks/components/ui/divider";
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerHandle,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from "@bidbattles/bloks/components/ui/drawer";
import { useViteEnvironment } from "@bidbattles/bloks/hooks/dev";
import {
	BBBackendClient,
	BBBackendQueryKeys,
} from "@bidbattles/bloks/lib/rapace";
import { cn } from "@bidbattles/bloks/lib/ui";
import { formOptions } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence } from "motion/react";
import { useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { z } from "zod/v4";

const loginFormSchema = z.object({
	email: z.email(),
	password: z.string(),
	remember: z.boolean(),
});
const loginFormOptions = formOptions({
	defaultValues: { email: "", password: "", remember: false },
	validators: {
		onChange: loginFormSchema,
		onBlur: loginFormSchema,
		onSubmit: loginFormSchema,
	},
});

const signupFormSchema = z.object({
	first_name: z.string(),
	last_name: z.string(),
	email: z.email(),
	password: z.string().min(8),
	referral_code: z.string(),
});
const signupFormOptions = (referral_code?: string) =>
	formOptions({
		defaultValues: {
			first_name: "",
			last_name: "",
			email: "",
			password: "",
			referral_code: referral_code ?? "",
		},
		validators: {
			onChange: signupFormSchema,
			onBlur: signupFormSchema,
			onSubmit: signupFormSchema,
		},
	});

const forgotPasswordFormSchema = z.object({
	email: z.email(),
});
const forgotPasswordFormOptions = formOptions({
	defaultValues: {
		email: "",
	},
	validators: {
		onChange: forgotPasswordFormSchema,
		onBlur: forgotPasswordFormSchema,
		onSubmit: forgotPasswordFormSchema,
	},
});

const resetPasswordFormSchema = z
	.object({
		token: z.string(),
		new_password: z.string(),
		confirm_new_password: z.string(),
	})
	.refine(
		({ new_password, confirm_new_password }) =>
			new_password === confirm_new_password,
		{
			error: "New password & new password confirmation are not equal",
			path: ["confirm_new_password"],
		},
	);
const resetPasswordFormOptions = (token: string) =>
	formOptions({
		defaultValues: {
			token,
			new_password: "",
			confirm_new_password: "",
		},
		validators: {
			onChange: resetPasswordFormSchema,
			onBlur: resetPasswordFormSchema,
			onSubmit: resetPasswordFormSchema,
		},
	});

type AuthDialogMode = "login" | "signup" | "forgot_password" | "reset_password";
type AuthDialogModeProps = {
	title: string;
	subtitle: string;
	hidden: boolean;
	trigger?: React.ReactElement<Record<string, unknown>>;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	isAtLeastMedium: boolean;
	setMode: (mode: AuthDialogMode) => void;
};

export function AuthDialog({
	hidden = false,
	trigger,
	defaultMode = "login",
	referralCode,
	defaultOpen,
	resetPasswordToken,
}: React.PropsWithChildren<{
	hidden?: boolean;
	trigger?: React.ReactElement<Record<string, unknown>>;
	defaultMode?: AuthDialogMode;
	referralCode?: string;
	defaultOpen?: boolean;
	resetPasswordToken?: string;
}>) {
	const queryClient = useQueryClient();

	const [mode, setMode] = useState(defaultMode);
	const [open, setOpen] = useState(defaultOpen ?? false);
	const isAtLeastMedium = useMininumDeviceWidthBreakpoint({
		breakpoint: "md",
		serverDefault: true,
	});

	const modes = {
		login: {
			title: m.auth__login(),
			subtitle: m.auth__login__subtitle(),
		},
		signup: {
			title: m.auth__signup(),
			subtitle: m.auth__signup__subtitle(),
		},
		forgot_password: {
			title: m.auth__forgot_password__title(),
			subtitle: m.auth__forgot_password__subtitle(),
		},
		reset_password: {
			title: m.auth__reset_password__title(),
			subtitle: m.auth__reset_password__subtitle(),
		},
	} as const;

	const modeInfo = modes[mode];

	const props = {
		title: modeInfo.title,
		subtitle: modeInfo.subtitle,
		hidden,
		trigger,
		open,
		onOpenChange: setOpen,
		isAtLeastMedium,
		setMode,
	};

	useEffect(() => {
		if (open) {
			return () => {
				setTimeout(() => {
					setMode(defaultMode);
					loginForm.reset();
					signupForm.reset();
					forgotPasswordForm.reset();
					resetPasswordForm.reset();
				}, 200);
			};
		}
	}, [open, defaultMode]);

	function invalidateQueries() {
		queryClient.invalidateQueries({
			predicate: () => true,
		});
	}

	const loginForm = useAppForm({
		...loginFormOptions,
		async onSubmit({ value }) {
			const result = await BBIsomorphicAuthClient.login(value);
			if (!result.ok) {
				toast.error(`An error occurred: ${result.error.message}`);
				return;
			}

			invalidateQueries();
			toast.success("Logged in successfully.");

			setOpen(false);
		},
	});

	const signupForm = useAppForm({
		...signupFormOptions(referralCode),
		async onSubmit({ value }) {
			const params = {
				...value,
				referral_code:
					value.referral_code.trim().length === 0
						? undefined
						: value.referral_code,
			};

			const result = await BBIsomorphicAuthClient.signup(value);
			if (!result.ok) {
				toast.error(`An error occurred: ${result.error.message}`);
				return;
			}

			invalidateQueries();
			toast.success("Signed up successfully.");

			setOpen(false);
		},
	});

	const forgotPasswordForm = useAppForm({
		...forgotPasswordFormOptions,
		async onSubmit({ value }) {
			const result = await BBBackendClient.sendResetPasswordToken(value);
			if (!result.ok) {
				toast.error(m.error_toast({ message: result.error.message }));
				return;
			}

			toast.success(
				"We've sent an email to you. If you can't find the mail, double-check the provided email, and your spam folder.",
				{
					duration: 5000,
				},
			);
			setMode("reset_password");
			setTimeout(() => forgotPasswordForm.reset(), 200);
		},
	});

	const resetPasswordForm = useAppForm({
		...resetPasswordFormOptions(resetPasswordToken ?? ""),
		async onSubmit({ value }) {
			const result = await BBBackendClient.resetPassword(value);
			if (!result.ok) {
				toast.error(m.error_toast({ message: result.error.message }));
				return;
			}

			toast.success(
				"Password changed successfully, you may proceed to login with your new password.",
			);
			setMode("login");
			setTimeout(() => resetPasswordForm.reset(), 200);
		},
	});

	let child: React.ReactNode = null;
	switch (mode) {
		case "login":
			child = (
				<Login {...props}>
					<LoginForm form={loginForm} setMode={setMode} />
				</Login>
			);
			break;
		case "signup":
			child = (
				<Signup {...props}>
					<SignupForm form={signupForm} />
				</Signup>
			);
			break;
		case "forgot_password":
			child = (
				<ForgotPassword {...props}>
					<ForgotPasswordForm form={forgotPasswordForm} />
				</ForgotPassword>
			);
			break;
		case "reset_password":
			child = (
				<ResetPassword {...props}>
					<ResetPasswordForm form={resetPasswordForm} />
				</ResetPassword>
			);
	}

	return (
		<AuthModalDrawer {...props}>
			<AnimatePresence mode="wait">{child}</AnimatePresence>
		</AuthModalDrawer>
	);
}

function AuthModalDrawer({
	title,
	subtitle,
	open,
	onOpenChange,
	isAtLeastMedium,
	hidden,
	trigger,
	children,
}: React.PropsWithChildren<AuthDialogModeProps>) {
	return (
		<>
			<Dialog
				open={open && isAtLeastMedium}
				onOpenChange={(open) => {
					isAtLeastMedium && onOpenChange(open);
				}}
			>
				<DialogTrigger
					className={cn("hidden", !hidden && "md:inline-flex")}
					render={trigger}
				/>
				<DialogContent>
					<DialogHeader className="mb-8">
						<DialogTitle>{title}</DialogTitle>
						<DialogDescription>{subtitle}</DialogDescription>
					</DialogHeader>
					{children}
				</DialogContent>
			</Dialog>
			<Drawer
				open={open && !isAtLeastMedium}
				onOpenChange={(open) => {
					!isAtLeastMedium && onOpenChange(open);
				}}
			>
				<DrawerTrigger asChild className={cn("md:hidden", hidden && "hidden")}>
					{trigger}
				</DrawerTrigger>
				<DrawerContent handle={<DrawerHandle />}>
					<DrawerHeader className="mb-8">
						<DrawerTitle>{title}</DrawerTitle>
						<DrawerDescription>{subtitle}</DrawerDescription>
					</DrawerHeader>
					{children}
				</DrawerContent>
			</Drawer>
		</>
	);
}

function Login({
	setMode,
	children,
}: React.PropsWithChildren<Pick<AuthDialogModeProps, "setMode">>) {
	const signupPrompt = (
		<p className="inline-flex justify-center text-center text-gray-500 text-xs md:text-sm w-full">
			{m.auth__signup_prompt()}&nbsp;
			<Button
				variant="link"
				onClick={() => setMode("signup")}
				className="text-accent-primary"
			>
				{m.auth__signup()}
			</Button>
		</p>
	);
	return (
		<>
			{children}
			{signupPrompt}
		</>
	);
}

const LoginForm = withForm({
	...loginFormOptions,
	props: { setMode: (mode: AuthDialogMode) => {} },
	render: function LoginForm({ form, setMode }) {
		const id = useId();
		const env = useViteEnvironment();

		return (
			<form
				onSubmit={(e) => {
					e.preventDefault();
					form.handleSubmit();
				}}
				className="flex flex-col gap-5 mb-8"
			>
				<form.AppField name="email">
					{(field) => <field.TextField label={m.auth__email()} />}
				</form.AppField>
				<form.AppField name="password">
					{(field) => (
						<field.TextField type="password" label={m.auth__password()} />
					)}
				</form.AppField>
				<div className="flex justify-between gap-4">
					<form.AppField name="remember">
						{(field) => (
							<label
								htmlFor={`checkbox:${id}`}
								className="flex gap-2 text-gray-500 text-xs md:text-sm"
							>
								<field.Checkbox id={`checkbox:${id}`} />
								{m.auth__remember_me()}
							</label>
						)}
					</form.AppField>
					<Button
						variant="link"
						className="text-xs md:text-sm"
						onClick={() => {
							setMode("forgot_password");
						}}
					>
						Forgot Password?
					</Button>
				</div>
				<form.AppForm>
					<form.SubscribeButton label={m.auth__login()} />
				</form.AppForm>
			</form>
		);
	},
});

function Signup({
	setMode,
	children,
}: React.PropsWithChildren<Pick<AuthDialogModeProps, "setMode">>) {
	const loginPrompt = (
		<p className="inline-flex justify-center text-center text-gray-500 text-xs md:text-sm w-full">
			{m.auth__login_prompt()}&nbsp;
			<Button
				variant="link"
				onClick={() => setMode("login")}
				className="text-accent-primary"
			>
				{m.auth__login()}
			</Button>
		</p>
	);
	return (
		<>
			{children}
			{loginPrompt}
		</>
	);
}

const SignupForm = withForm({
	...signupFormOptions(""),
	render: function SignupForm({ form }) {
		return (
			<form
				onSubmit={(e) => {
					e.preventDefault();
					form.handleSubmit();
				}}
				className="flex flex-col gap-5 mb-8"
			>
				<div className="grid grid-cols-2 gap-4">
					<form.AppField name="first_name">
						{(field) => <field.TextField label={m.auth__first_name()} />}
					</form.AppField>
					<form.AppField name="last_name">
						{(field) => <field.TextField label={m.auth__last_name()} />}
					</form.AppField>
				</div>
				<form.AppField name="email">
					{(field) => <field.TextField label={m.auth__email()} />}
				</form.AppField>
				<div className="flex flex-col gap-2">
					<form.AppField name="password">
						{(field) => (
							<field.TextField type="password" label={m.auth__password()} />
						)}
					</form.AppField>
					<p className="text-xs md:text-sm text-gray-500">
						{m.auth__password_instructions()}
					</p>
				</div>
				<form.AppField name="referral_code">
					{(field) => <field.TextField label={m.auth__referral_code()} />}
				</form.AppField>
				<p className="text-xs md:text-sm text-gray-500">
					{m.auth__signup__terms()}
				</p>
				<form.AppForm>
					<form.SubscribeButton label={m.auth__signup()} />
				</form.AppForm>
			</form>
		);
	},
});

function ForgotPassword({
	setMode,
	children,
}: React.PropsWithChildren<Pick<AuthDialogModeProps, "setMode">>) {
	const otpPrompt = (
		<p className="inline-flex justify-center text-center text-gray-500 text-xs md:text-sm w-full">
			Already have a code?&nbsp;
			<Button
				variant="link"
				onClick={() => setMode("reset_password")}
				className="text-accent-primary"
			>
				Click here
			</Button>
		</p>
	);

	return (
		<>
			{children}
			{otpPrompt}
		</>
	);
}

const ForgotPasswordForm = withForm({
	...forgotPasswordFormOptions,
	render: function SignupForm({ form }) {
		return (
			<form
				onSubmit={(e) => {
					e.preventDefault();
					form.handleSubmit();
				}}
				className="flex flex-col gap-5 mb-8"
			>
				<form.AppField name="email">
					{(field) => <field.TextField label={m.auth__email()} />}
				</form.AppField>
				<form.AppForm>
					<form.SubscribeButton label={m.auth__send()} />
				</form.AppForm>
			</form>
		);
	},
});

function ResetPassword({
	setMode,
	children,
}: React.PropsWithChildren<Pick<AuthDialogModeProps, "setMode">>) {
	const otpPrompt = (
		<p className="inline-flex justify-center text-center text-gray-500 text-xs md:text-sm w-full">
			Don't have a code?&nbsp;
			<Button
				variant="link"
				onClick={() => setMode("forgot_password")}
				className="text-accent-primary"
			>
				Get one
			</Button>
		</p>
	);
	return (
		<>
			{children}
			{otpPrompt}
		</>
	);
}

const ResetPasswordForm = withForm({
	...resetPasswordFormOptions(""),
	render: function SignupForm({ form }) {
		return (
			<form
				onSubmit={(e) => {
					e.preventDefault();
					form.handleSubmit();
				}}
				className="flex flex-col gap-5 mb-8"
			>
				<form.AppField name="token">
					{(field) => <field.TextField label="OTP" />}
				</form.AppField>
				<form.AppField name="new_password">
					{(field) => (
						<field.TextField type="password" label={m.auth__new_password()} />
					)}
				</form.AppField>
				<form.AppField name="confirm_new_password">
					{(field) => (
						<field.TextField
							type="password"
							label={m.auth__confirm_new_password()}
						/>
					)}
				</form.AppField>
				<form.AppForm>
					<form.SubscribeButton label={m.auth__reset_password__submit()} />
				</form.AppForm>
			</form>
		);
	},
});
